// backendServicesManagment

// backendServicesManagment
export interface BackendResponse<T = any> {
  code: number;
  comment: string;
  payload: T;  // Ajout du type générique pour le payload
}

export const initializedBackendResponse = (): BackendResponse => ({
  code: 0,
  comment: "",
  payload: {} as any, // Type générique par défaut
});

export const handleError = <T>(status: number, error: string, payload: T = {} as T): BackendResponse<T> => ({
  code: status,
  comment: error,
  payload,
});

/**
 * Code rendu par un service quand l'API refuse un nom déjà pris.
 *
 * Négatif comme les autres codes d'erreur du front (-101, -102), et distinct d'eux
 * pour que l'écran puisse dire « Cette catégorie existe déjà » au lieu du « une erreur
 * est survenue » qui couvre tout le reste.
 */
export const DUPLICATE_NAME_CODE = -409;

/**
 * Code rendu par AdminCreation quand l'e-mail correspond déjà à un User existant :
 * la création est bloquée (HTTP 409, `requiresPromotionConfirmation: true`) tant que
 * l'appelant n'a pas confirmé qu'il s'agit d'une promotion de ce compte en créateur.
 */
export const PROMOTION_CONFIRMATION_REQUIRED_CODE = -110;

/**
 * Code rendu par AdminDeleting quand le compte créateur à supprimer est lié à un User
 * existant : la suppression est bloquée (HTTP 409, `requiresUserDeletionConfirmation:
 * true`) tant que l'appelant n'a pas précisé s'il faut aussi supprimer ce User
 * (`deleteAssociatedUser=true`) ou seulement rétrograder le compte (`=false`).
 */
export const USER_DELETION_CONFIRMATION_REQUIRED_CODE = -111;

/**
 * Code rendu par AdminDeleting quand le compte créateur à supprimer est déjà
 * pseudo-supprimé (désactivé, email suffixé) et possède encore des chaînes ou des
 * médias : la suppression reste refusée (HTTP 409, `alreadyDisabled: true`), avec un
 * message explicatif donnant le nombre de chaînes/médias qui la bloquent.
 */
export const ALREADY_PSEUDO_DELETED_CODE = -112;

/**
 * Le refus d'un doublon se reconnaît-il dans cette réponse d'erreur ?
 *
 * L'API ne répond pas 409 mais **400** : le contrôle d'unicité passe par
 * `Trace::writeToErrorFile`, qui lève un `BadRequestHttpException`. Le statut est donc
 * celui de tous les autres refus de validation et ne distingue rien — c'est le message,
 * déjà rédigé en français côté API (« Vous avez déjà une catégorie portant ce nom »),
 * qui porte l'information.
 *
 * Reconnu sur son noyau invariant, sans la ponctuation ni la personne : les contrôleurs
 * de création et de renommage n'écrivent pas exactement la même phrase.
 */
export const isDuplicateNameRefusal = ( status: number | undefined , detail: string ): boolean =>
  status === 400 && /déjà une catégorie/i.test(detail);

/**
 * Le message d'un refus, tel que l'API l'a rédigé.
 *
 * API Platform 3.2 le pose dans `hydra:description` quand la réponse est négociée en
 * JSON-LD — ce que le front demande partout — et dans `detail` dans les autres formats.
 * Lire les deux évite de dépendre du format : à défaut, on ne récupérait que le
 * « Request failed with status code 400 » d'axios, où il n'y a rien à comprendre.
 */
export const extractApiErrorDetail = ( data: any , fallback: string ): string => {
  if (data && typeof data.detail === 'string' && data.detail.trim() !== '') { return data.detail; }

  if (data && typeof data['hydra:description'] === 'string' && data['hydra:description'].trim() !== '') { return data['hydra:description']; }

  return fallback;
};

/**
 * Message affichable tiré du corps d'un refus 409.
 *
 * Les endpoints de suppression répondent par une phrase française déjà rédigée pour
 * l'utilisateur — elle nomme la chaîne et l'horaire quand le média est au programme. Le
 * corps peut aussi être un objet API Platform (`detail`), ou vide si le refus vient d'une
 * couche qui n'explique rien : d'où le repli générique.
 */
export const extractServerConflictMessage = ( data: any ): string => {
  if (typeof data === 'string' && data.trim() !== '') { return data; }

  if (data && typeof data.detail === 'string' && data.detail.trim() !== '') { return data.detail; }

  return "Impossible de supprimer le média car il est déjà lié à d'autres ressources";
};
